// The things a brand says in every second post, typed once.
//
// The pitch on the screen is the whole design: put the booking link here, write
// {{booking}} in a caption, and never think about it again. When the link
// changes you change it here, and every caption that mentions it is already
// right — including the two hundred you imported from a spreadsheet last month.
//
// SCOPE IS THE SECOND HALF OF IT, and it is the part that has to be on screen
// rather than inferred. A workspace holds several brands (ADR-0029): labtest1's
// phone number is not sabu's, but the support email and the company name very
// often are. So every value is either one brand's or shared by all of them, a
// brand's own answer wins over the shared one, and the selector at the top of
// this card says which of the two you are editing before you type. A card that
// guessed from the top bar's filter would be a card that writes sabu's number
// into labtest1 whenever somebody had "All brands" selected.
//
// A shared value is shown on a brand's list too, greyed, with "Use a different
// one here" beside it — because the question being asked at that moment is
// "what will {{phone}} become for this brand", and an inherited value that is
// invisible until it appears in a published post is the wrong answer to it.
//
// Editing is per row and explicit. A grid of always-live inputs would save a
// click and cost the thing that makes this trustworthy: these values are about
// to be substituted into text that goes out under somebody's name, and a stray
// keystroke in a table cell must not be able to change forty future posts
// without anybody pressing anything.

import { useMemo, useState } from "react";
import { deleteBrandDetail, saveBrandDetail } from "../lib/api";
import { SUGGESTED_DETAILS, effectiveDetails, keyProblem } from "../lib/tokens";
import { Spinner } from "./Spinner";
import type { Brand } from "../lib/brands";
import type { BrandDetail } from "../lib/types";

export function BrandDetails({
  workspaceId, brands, details, mayWrite, defaultBrandId, onChanged,
}: {
  workspaceId: string;
  /** Every brand in the workspace, so a value can be filed under one of them. */
  brands: Brand[];
  /** Every row the workspace has — both scopes. The card picks from them. */
  details: BrandDetail[];
  mayWrite: boolean;
  /** The brand chosen in the top bar, if one is. Null opens on the shared set. */
  defaultBrandId: string | null;
  onChanged: () => void | Promise<void>;
}) {
  // Which brand these values are for. Null is the workspace's shared set, which
  // is also where a workspace with one brand and nothing chosen starts —
  // typing a phone number there gets it to every brand, which is what somebody
  // running one business means.
  const [scope, setScope] = useState<string | null>(defaultBrandId);
  // Which row is open for editing, by id — or "new" for the row being added.
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState({ key: "", label: "", value: "" });
  // Keyed rather than boolean: saving one row must not spin the button on the
  // row beside it.
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scopeName = scope ? brands.find((b) => b.id === scope)?.name ?? null : null;

  // What this scope actually resolves to, inherited rows included. On the
  // shared set that is just the shared rows; on a brand it is its own values
  // plus the shared ones it has not answered itself.
  const visible = useMemo(
    () => (scope === null
      ? details.filter((d) => d.brand_id === null)
      : effectiveDetails(details, scope)),
    [details, scope],
  );

  const taken = new Set(visible.map((d) => d.key));
  const unused = SUGGESTED_DETAILS.filter((s) => !taken.has(s.key));

  function open(detail: BrandDetail) {
    setError(null);
    setEditing(detail.id);
    setDraft({ key: detail.key, label: detail.label, value: detail.value });
  }

  /** Take a shared value and give this brand its own. Same key, new row. */
  function override(detail: BrandDetail) {
    setError(null);
    setEditing("new");
    setDraft({ key: detail.key, label: detail.label, value: detail.value });
  }

  function openNew(key: string) {
    const suggestion = SUGGESTED_DETAILS.find((s) => s.key === key);
    setError(null);
    setEditing("new");
    setDraft({
      key: suggestion?.key ?? "",
      label: suggestion?.label ?? "",
      value: "",
    });
  }

  function changeScope(next: string | null) {
    // An open editor belongs to the scope it was opened in. Carrying it across
    // would save whatever is half-typed into the other brand.
    setEditing(null);
    setError(null);
    setScope(next);
  }

  async function save(id: string) {
    const problem = keyProblem(draft.key);
    if (problem) { setError(problem); return; }
    if (!draft.label.trim()) { setError("Give it a label — that is what you will see here."); return; }
    if (!draft.value.trim()) { setError("Give it a value, or delete the detail instead."); return; }

    setBusy(id);
    setError(null);
    try {
      await saveBrandDetail({
        workspaceId,
        brandId: scope,
        key: draft.key,
        label: draft.label.trim(),
        value: draft.value.trim(),
        sortOrder: SUGGESTED_DETAILS.find((s) => s.key === draft.key)?.sort ?? 100,
      });
      setEditing(null);
      await onChanged();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  async function remove(detail: BrandDetail) {
    setBusy(detail.id);
    setError(null);
    try {
      await deleteBrandDetail(detail.id);
      await onChanged();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  const fields = (id: string) => (
    <div className="detail-form">
      <div className="detail-form-row">
        <label>
          <span>Label</span>
          <input value={draft.label} placeholder="Booking link"
                 onChange={(e) => setDraft({ ...draft, label: e.target.value })} />
        </label>
        <label>
          <span>Write this in a caption</span>
          <input className="mono" value={draft.key} placeholder="booking"
                 // The key is what existing captions point at. Renaming it here
                 // would leave every `{{old}}` unresolved, so an existing detail
                 // keeps its name — delete and re-add if it really must change.
                 disabled={id !== "new"}
                 onChange={(e) => setDraft({ ...draft, key: e.target.value.toLowerCase() })} />
        </label>
      </div>
      <label>
        <span>Value</span>
        <input value={draft.value}
               placeholder={SUGGESTED_DETAILS.find((s) => s.key === draft.key)?.example ?? ""}
               onChange={(e) => setDraft({ ...draft, value: e.target.value })} />
      </label>
      <div className="row">
        <button className="primary" onClick={() => save(id)} disabled={busy === id}>
          {busy === id && <Spinner />} {busy === id ? "Saving…" : "Save"}
        </button>
        <button onClick={() => { setEditing(null); setError(null); }} disabled={busy === id}>
          Cancel
        </button>
        {draft.key && !keyProblem(draft.key) && (
          <span className="muted">
            {scopeName
              ? <>Captions say <code>{`{{${draft.key}}}`}</code>, and for {scopeName} it becomes this</>
              : <>Captions can now say <code>{`{{${draft.key}}}`}</code></>}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div className="card">
      <h2>Brand details</h2>
      <p className="muted">
        Type these once. Write <code>{"{{booking}}"}</code> in a caption and it becomes the real
        link the moment you pick that caption in Compose — so when the link changes, you change it
        here and every caption is already right.
      </p>

      {brands.length > 0 && (
        <div className="row detail-scope">
          <label htmlFor="detail-scope">These are for</label>
          <select id="detail-scope" className="caption-library-select" value={scope ?? ""}
                  onChange={(e) => changeScope(e.target.value || null)}>
            <option value="">Every brand here — shared</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>{b.name} only</option>
            ))}
          </select>
        </div>
      )}

      <p className="muted card-sub">
        {scopeName
          ? <>
              What <strong>{scopeName}</strong> resolves to. Values shared by the whole workspace
              are shown too — give {scopeName} its own and that one wins for {scopeName} alone.
            </>
          : <>
              The workspace's answers, used by every brand that has not set its own. Pick a brand
              above to give it a different phone number or a different app link.
            </>}
      </p>

      {error && <div className="err">{error}</div>}

      {visible.length === 0 && editing !== "new" ? (
        <p className="muted" style={{ marginTop: ".8rem" }}>
          Nothing here yet. Add your booking link or phone number and stop retyping them.
        </p>
      ) : (
        <table className="data-table detail-table">
          <thead>
            <tr>
              <th>Detail</th>
              <th>In a caption</th>
              <th>Value</th>
              {mayWrite && <th className="col-actions"><span className="visually-hidden">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {visible.map((d) => {
              // On a brand's list, a row still filed under no brand is the
              // workspace's answer showing through.
              const inherited = scope !== null && d.brand_id === null;

              return editing === d.id ? (
                <tr key={d.id}><td colSpan={mayWrite ? 4 : 3}>{fields(d.id)}</td></tr>
              ) : (
                <tr key={d.id} className={inherited ? "is-inherited" : undefined}>
                  <td>
                    {d.label}
                    {inherited && <span className="muted detail-scope-tag"> · shared</span>}
                  </td>
                  <td><code className="token">{`{{${d.key}}}`}</code></td>
                  <td className="detail-value">{d.value}</td>
                  {mayWrite && (
                    <td className="col-actions">
                      {inherited ? (
                        <button className="linkish" onClick={() => override(d)}
                                title={`Give ${scopeName} its own ${d.label.toLowerCase()}`}>
                          Use a different one here
                        </button>
                      ) : (
                        <>
                          <button className="linkish" onClick={() => open(d)}>Edit</button>
                          <button className="linkish danger-link" onClick={() => remove(d)}
                                  disabled={busy === d.id}>
                            {busy === d.id ? <Spinner label="Removing" /> : "Delete"}
                          </button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {editing === "new" && (
              <tr><td colSpan={mayWrite ? 4 : 3}>{fields("new")}</td></tr>
            )}
          </tbody>
        </table>
      )}

      {mayWrite && editing === null && (
        <div className="row" style={{ marginTop: ".9rem" }}>
          <label className="visually-hidden" htmlFor="add-detail">Add a brand detail</label>
          <select id="add-detail" className="caption-library-select" value="" onChange={(e) => openNew(e.target.value)}>
            <option value="" disabled>
              {scopeName ? `Add a detail for ${scopeName}…` : "Add a shared detail…"}
            </option>
            {unused.map((s) => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
            <option value="__custom">Something else…</option>
          </select>
        </div>
      )}
    </div>
  );
}
